import { Button, Box, Chip, Link } from "@mui/material";
import ApproveChefForParty from "../Common/ApproveChefForParty";
import { useState } from "react";
import ChefForPartStatusEnum from "../../enums/ChefForPartStatusEnum";
import ChefForPartyService from "../../services/ChefForPartyService";
import CallButton from "../Common/CallButton/CallButton";
import { moduleType, UserType } from "../../enums/common";
import { formatDateTimeWithSeconds } from '../../utils/DateTimeUtils';
import PartyCookExtraDetails from "../Common/PartyCookExtraDetails";
import ImagePreviewerDialog from "../Common/ImagePreviewerDialog";
import CookDenyDialog from "../Common/CookDenyDialog";
import {
    WorkspacePanel,
    WorkspaceSection,
    SectionHeader,
    WorkSpaceTitleRow,
    SectionTitle,
    ThreeColGrid,
    HorizontalContainer,
    ShowLabel,
    ShowValue,
    StatusBooleanBadge,
    InfoPill,
    HighlightValue,
    HorizontalLine,
} from "../../styles/commonStyles";
import { colors } from "../../styles/colors";
import { ChefCapIcon } from "../Svg/ChefCapIcon";
import { UserIcon, CalendarIcon, ClockIcon } from "../Svg";

/* ─── Media Thumbnails ─────────────────────────────────────────── */
const MediaThumbs = ({ media = [], onClickItem }: { media: string[]; onClickItem: (images: string[], index: number) => void }) => {
    if (!media?.length) return null;
    return (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 0.4, overflowX: "auto", pb: 0.5, maxWidth: "100%" }}>
            {media.map((url, i) => {
                const isVideo = /\.(mp4|webm|ogg)$/i.test(url);
                return (
                    <Box key={i} onClick={() => onClickItem(media, i)} sx={{
                        width: 36, height: 36, borderRadius: 0.75, overflow: "hidden",
                        position: "relative", background: "#f0f0f0", cursor: "pointer",
                        border: "1px solid #e0e0e0", flexShrink: 0,
                        "&:hover": { opacity: 0.75 },
                    }}>
                        {isVideo ? (
                            <>
                                <video src={url} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <Box sx={{
                                    position: "absolute", top: "50%", left: "50%",
                                    transform: "translate(-50%,-50%)", fontSize: 9,
                                    color: "white", background: "rgba(0,0,0,0.5)",
                                    width: 16, height: 16, borderRadius: "50%",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>▶</Box>
                            </>
                        ) : (
                            <img src={url} alt="media" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

/* ─── Main Component ───────────────────────────────────────────── */
const ChefForPartyCooksList = (props: any) => {
    const [selectedData, setSelectedData] = useState<any>();
    const [approvalModel, setApprovalModel] = useState<boolean>(false);
    const [cookDetailsId, setCookDetailsId] = useState<number>(0);
    const chefForPartyService = new ChefForPartyService();
    const [openExtraDetails, setOpenExtraDetails] = useState<boolean>(false);
    const [cookData, setCookData] = useState<any>({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [openCookDenyDialog, setOpenCookDenyDialog] = useState<boolean>(false);

    const handleAccept = (data: any, detailsId: number) => {
        setApprovalModel(true);
        setSelectedData(data);
        setCookDetailsId(detailsId);
    };

    const handleRemoveCook = (data: any) => {
        chefForPartyService.removeCook(props?.party?.id, data?.cookId).then(() => {
            props.refresh();
        });
    };

    const handlePreCheckIn = (data: any) => {
        chefForPartyService.preCheckIn(data?.chefForPartyId, data?.cookId).then(() => {
            props.refresh();
        });
    };

    const openPreview = (images: string[], index: number) => {
        setPreviewImages(images);
        setPreviewIndex(index);
        setPreviewOpen(true);
    };

    const canRemove = ![
        ChefForPartStatusEnum.CANCELLED,
        ChefForPartStatusEnum.ONGOING,
        ChefForPartStatusEnum.RESCHEDULED,
        ChefForPartStatusEnum.FULFILLED,
    ].includes(props?.party?.status);

    return (
        <>
            <ApproveChefForParty
                data={selectedData}
                cookDetailsId={cookDetailsId}
                open={approvalModel}
                refresh={props.refresh}
                callback={() => setApprovalModel(false)}
            />
            <CookDenyDialog
                open={openCookDenyDialog}
                setOpen={setOpenCookDenyDialog}
                data={cookData}
                requestFrom={moduleType.CHEF_FOR_PARTY}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {props.data.map((row: any) => {
                    const profile = row?.profile?.cookFullProfile;
                    const details = row?.details;

                    const ackColor =
                        details?.cookAcknowledge === true ? "success" :
                        details?.cookAcknowledge === false ? "error" : "default";
                    const ackLabel =
                        details?.cookAcknowledge === true ? "Accepted" :
                        details?.cookAcknowledge === false ? "Rejected" : "Pending";

                    const dishImages: string[] = Array.isArray(details?.dishImages)
                        ? details.dishImages : [];

                    return (
                        <WorkspacePanel key={row?.profile?.id} style={{ padding: "14px 16px" }}>

                            {/* ── Cook Header ──────────────────────── */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <ChefCapIcon color={colors.primary} height={22} width={22} />
                                <SectionTitle style={{ margin: 0 }}>
                                    {(profile?.firstName || "—") + " " + (row?.cookFullProfile?.lastName || "")}
                                </SectionTitle>
                                {details?.type && (
                                    <Chip
                                        label={details.type}
                                        size="small"
                                        sx={{ fontWeight: 600, height: 22, fontSize: "0.72rem" }}
                                    />
                                )}
                                <Chip
                                    label={ackLabel}
                                    color={ackColor as any}
                                    size="small"
                                    sx={{ fontWeight: 600, height: 22, fontSize: "0.72rem" }}
                                />
                            </Box>

                            <ThreeColGrid style={{ marginTop: "6px" }}>
                                <HorizontalContainer>
                                    <UserIcon color="#6C7C93" width={16} height={16} />
                                    <ShowLabel style={{ marginBottom: "0px" }}>Cook ID:</ShowLabel>
                                    <Link href={`/#/Cooks/${profile?.cookId}/show`} underline="hover">
                                        <ShowValue style={{ color: colors.primary, fontWeight: 600 }}>
                                            {profile?.cookId || "—"}
                                        </ShowValue>
                                    </Link>
                                </HorizontalContainer>
                                <HorizontalContainer>
                                    <ShowLabel style={{ marginBottom: "0px" }}>Gender:</ShowLabel>
                                    <ShowValue>{profile?.gender || "—"}</ShowValue>
                                </HorizontalContainer>
                                <HorizontalContainer>
                                    <ShowLabel style={{ marginBottom: "0px" }}>Mode of Travel:</ShowLabel>
                                    <ShowValue>{profile?.modeOfTravel || "—"}</ShowValue>
                                </HorizontalContainer>
                            </ThreeColGrid>

                            <ThreeColGrid style={{ marginTop: "8px" }}>
                                <HorizontalContainer>
                                    <ShowLabel style={{ marginBottom: "0px" }}>Non-Veg:</ShowLabel>
                                    <StatusBooleanBadge active={profile?.cooksNonVeg}>
                                        {profile?.cooksNonVeg ? "Yes" : "No"}
                                    </StatusBooleanBadge>
                                </HorizontalContainer>
                                <HorizontalContainer>
                                    <ShowLabel style={{ marginBottom: "0px" }}>Mobile:</ShowLabel>
                                    {details?.cookPhone ? (
                                        <CallButton
                                            toPhoneNumber={details.cookPhone}
                                            userType={UserType.COOK}
                                            referenceId={details?.chefForPartyId}
                                            referenceModelName="CHEF_FOR_PARTY"
                                        />
                                    ) : (
                                        <ShowValue>—</ShowValue>
                                    )}
                                </HorizontalContainer>
                            </ThreeColGrid>

                            <HorizontalLine style={{ margin: "10px 0" }} />

                            {/* ── Payment Details ───────────────────── */}
                            <WorkspaceSection style={{ marginBottom: "6px" }}>
                                <SectionHeader>
                                    <WorkSpaceTitleRow>
                                        <SectionTitle>Payment Details</SectionTitle>
                                    </WorkSpaceTitleRow>
                                </SectionHeader>
                            </WorkspaceSection>

                            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                <InfoPill>
                                    <ShowLabel style={{ fontSize: "14px", fontWeight: "normal", marginBottom: 0 }}>Cook Cut</ShowLabel>
                                    <HighlightValue style={{ fontSize: "14px" }}>{details?.cookCut ?? "—"}</HighlightValue>
                                </InfoPill>
                                <InfoPill style={{ backgroundColor: "rgb(255, 251, 235)", border: "1px solid rgb(180, 83, 9, 0.15)" }}>
                                    <ShowLabel style={{ fontSize: "14px", fontWeight: "normal", marginBottom: 0 }}>Travel Allowance</ShowLabel>
                                    <HighlightValue style={{ fontSize: "14px", color: "rgb(180, 83, 9)" }}>{details?.travelAllowance ?? "—"}</HighlightValue>
                                </InfoPill>
                                <InfoPill style={{ backgroundColor: "rgb(240, 253, 250)", border: "1px solid rgb(15, 118, 110, 0.15)" }}>
                                    <ShowLabel style={{ fontSize: "14px", fontWeight: "normal", marginBottom: 0 }}>Extra Time Payment</ShowLabel>
                                    <HighlightValue style={{ fontSize: "14px", color: "rgb(15, 118, 110)" }}>{details?.extraTimePayment ?? "—"}</HighlightValue>
                                </InfoPill>
                                <InfoPill style={{ backgroundColor: "rgb(250, 245, 255)", border: "1px solid rgb(126, 34, 206, 0.15)" }}>
                                    <ShowLabel style={{ fontSize: "14px", fontWeight: "normal", marginBottom: 0 }}>Helper Cost</ShowLabel>
                                    <HighlightValue style={{ fontSize: "14px", color: "rgb(126, 34, 206)" }}>{details?.helperCost ?? "—"}</HighlightValue>
                                </InfoPill>
                            </div>

                            <HorizontalLine style={{ margin: "10px 0" }} />

                            {/* ── Attendance & Media ────────────────── */}
                            <WorkspaceSection style={{ marginBottom: "6px" }}>
                                <SectionHeader>
                                    <WorkSpaceTitleRow>
                                        <CalendarIcon color={colors.primary} height={18} width={18} />
                                        <SectionTitle>Attendance & Media</SectionTitle>
                                    </WorkSpaceTitleRow>
                                </SectionHeader>
                            </WorkspaceSection>

                            <ThreeColGrid style={{ marginTop: "6px" }}>
                                <HorizontalContainer>
                                    <ClockIcon color="#6C7C93" width={15} height={15} />
                                    <ShowLabel style={{ marginBottom: "0px" }}>Pre Check-In:</ShowLabel>
                                    <ShowValue>
                                        {details?.preCheckedInAt
                                            ? `${formatDateTimeWithSeconds(details.preCheckedInAt)} (${details.preCheckedInType})`
                                            : "Not Available"}
                                    </ShowValue>
                                </HorizontalContainer>
                                <div>
                                    <HorizontalContainer>
                                        <CalendarIcon color="#6C7C93" width={14} height={14} />
                                        <ShowLabel style={{ marginBottom: "0px" }}>Check-In:</ShowLabel>
                                        <ShowValue>
                                            {details?.checkInTime
                                                ? `${formatDateTimeWithSeconds(details.checkInTime)} (${details.checkinByType})`
                                                : "Not Available"}
                                        </ShowValue>
                                    </HorizontalContainer>
                                    {details?.checkInImage?.length > 0 && (
                                        <MediaThumbs media={details.checkInImage} onClickItem={(imgs, idx) => openPreview(imgs, idx)} />
                                    )}
                                </div>
                                <div>
                                    <HorizontalContainer>
                                        <CalendarIcon color="#6C7C93" width={14} height={14} />
                                        <ShowLabel style={{ marginBottom: "0px" }}>Check-Out:</ShowLabel>
                                        <ShowValue>
                                            {details?.checkOutTime
                                                ? `${formatDateTimeWithSeconds(details.checkOutTime)} (${details.checkoutByType})`
                                                : "Not Available"}
                                        </ShowValue>
                                    </HorizontalContainer>
                                    {details?.checkOutImage?.length > 0 && (
                                        <MediaThumbs media={details.checkOutImage} onClickItem={(imgs, idx) => openPreview(imgs, idx)} />
                                    )}
                                </div>
                            </ThreeColGrid>

                            {dishImages.length > 0 && (
                                <HorizontalContainer style={{ marginTop: "8px" }}>
                                    <ShowLabel style={{ marginBottom: "0px" }}>Dish Images:</ShowLabel>
                                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflowX: "auto", pb: 0.5, maxWidth: "100%" }}>
                                        {dishImages.map((url, i) => (
                                            <Box key={i} onClick={() => openPreview(dishImages, i)} sx={{
                                                width: 36, height: 36, borderRadius: 0.75, overflow: "hidden",
                                                cursor: "pointer", border: "1px solid #e0e0e0", background: "#f5f5f5",
                                                flexShrink: 0, "&:hover": { opacity: 0.75 },
                                            }}>
                                                <img src={url} alt={`dish-${i}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            </Box>
                                        ))}
                                    </Box>
                                </HorizontalContainer>
                            )}

                            <HorizontalLine style={{ margin: "10px 0" }} />

                            {/* ── Actions ──────────────────────────── */}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {details?.cookAcknowledge === null && (
                                    <Button variant="contained"
                                        sx={{ fontSize: "0.85rem", px: 2, py: 0.6, textTransform: "none", fontWeight: 600 }}
                                        onClick={() => handleAccept(profile, details?.id)}>
                                        Accept / Reject
                                    </Button>
                                )}
                                {canRemove && (
                                    <Button variant="contained" color="warning"
                                        sx={{ fontSize: "0.85rem", px: 2, py: 0.6, textTransform: "none", fontWeight: 600 }}
                                        onClick={() => handleRemoveCook(profile)}>
                                        Remove
                                    </Button>
                                )}
                                {!details?.preCheckedInAt && details?.cookAcknowledge === true && (
                                    <Button variant="contained" color="warning"
                                        sx={{ fontSize: "0.85rem", px: 2, py: 0.6, textTransform: "none", fontWeight: 600 }}
                                        onClick={() => handlePreCheckIn(details)}>
                                        Pre Check-In
                                    </Button>
                                )}
                                {details?.cookAcknowledge === true && (
                                    <Button variant="contained" color="warning"
                                        sx={{ fontSize: "0.85rem", px: 2, py: 0.6, textTransform: "none", fontWeight: 600 }}
                                        onClick={() => { setOpenExtraDetails(true); setCookData(details); }}>
                                        Edit
                                    </Button>
                                )}
                                <Button variant="outlined" color="error"
                                    sx={{ fontSize: "0.85rem", px: 2, py: 0.6, textTransform: "none", fontWeight: 600 }}
                                    onClick={() => { setOpenCookDenyDialog(true); setCookData(details); }}>
                                    Cook Deny
                                </Button>
                            </Box>

                        </WorkspacePanel>
                    );
                })}
            </Box>

            {openExtraDetails && (
                <PartyCookExtraDetails
                    callBack={() => setOpenExtraDetails(false)}
                    data={cookData}
                    open={openExtraDetails}
                    partyDate={props?.party?.date}
                />
            )}
            {previewOpen && (
                <ImagePreviewerDialog
                    src={previewImages[previewIndex] ?? ""}
                    images={previewImages}
                    initialIndex={previewIndex}
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </>
    );
};

export default ChefForPartyCooksList;
